import React, { useState, useEffect, useRef } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import './DicomGridViewer.css';

const DicomGridViewer = React.forwardRef(({ imageIds = [], files = [], isAHI = false, initialLayout = { rows: 2, cols: 2 }, onLayoutChange, dicomFiles = [], allStudyFiles = [], onOpenControls, drawerOpen = true, onActiveViewportChange }, ref) => {
    const [gridLayout, setGridLayout] = useState(initialLayout);
    const [activeViewportIndex, setActiveViewportIndex] = useState(0);
    const viewportRefs = useRef([]);
    const containerRef = useRef(null);
    const isLoadingRef = useRef(false);
    const loadedFilesRef = useRef([]);

    // Update grid layout when initialLayout prop changes
    useEffect(() => {
        setGridLayout(initialLayout);
        // Reset loading state when layout changes to allow reloading
        isLoadingRef.current = false;
        loadedFilesRef.current = [];
        
        // Force reload when layout changes by clearing the loaded files cache
        // This ensures that when switching layouts, all viewports get populated
        setTimeout(() => {
            isLoadingRef.current = false;
            loadedFilesRef.current = [];
        }, 50);
    }, [initialLayout]);

    // Initialize viewports when layout changes
    useEffect(() => {
        const totalViewports = gridLayout.rows * gridLayout.cols;
        viewportRefs.current = Array(totalViewports).fill(null).map((_, i) => viewportRefs.current[i] || React.createRef());
    }, [gridLayout]);

    // Load images into viewports
    useEffect(() => {
        const loadImagesIntoViewports = async () => {
            // Prevent infinite loops by checking if we're already loading
            if (isLoadingRef.current) {
                console.log('Already loading, skipping...');
                return;
            }

            const totalViewports = gridLayout.rows * gridLayout.cols;
            const filesToLoad = dicomFiles.length > 0 ? dicomFiles : (isAHI ? imageIds : files);
            
            // Only check for file changes if we have files to load
            if (filesToLoad.length === 0) {
                console.log('No files to load');
                return;
            }
            
            // Always reload when layout changes or when we have different files
            // Don't use caching for grid layout changes to ensure all viewports are populated
            const layoutKey = `${gridLayout.rows}x${gridLayout.cols}`;
            const availableFileIds = filesToLoad.slice(0, totalViewports).map(f => f?.id || f?.imageId || f?.name || f);
            const currentKey = `${layoutKey}:${availableFileIds.join('|')}`;
            
            // Only skip if we have the exact same key AND we're not changing layouts
            if (loadedFilesRef.current === currentKey && loadedFilesRef.current !== '') {
                console.log('Same layout and files already loaded, skipping...');
                return;
            }
            
            // Force reload for layout changes
            console.log(`Layout or files changed. Previous: ${loadedFilesRef.current}, Current: ${currentKey}`);

            console.log(`Loading files for ${layoutKey} layout:`, filesToLoad.slice(0, totalViewports).map(f => f?.instanceNumber || 'unknown'));
            console.log(`Total viewports: ${totalViewports}, Available files: ${filesToLoad.length}`);
            console.log('Files to load:', filesToLoad.slice(0, totalViewports));
            isLoadingRef.current = true;
            loadedFilesRef.current = currentKey; // Store the current key
            
            // Clean up all viewports first
            for (let j = 0; j < viewportRefs.current.length; j++) {
                const ref = viewportRefs.current[j];
                if (ref && ref.current) {
                    // Clear any child elements (videos)
                    while (ref.current.firstChild) {
                        ref.current.removeChild(ref.current.firstChild);
                    }
                    
                    try {
                        // Only try to disable if cornerstone is enabled
                        const enabledElement = cornerstone.getEnabledElement(ref.current);
                        if (enabledElement) {
                            cornerstone.disable(ref.current);
                        }
                    } catch (e) {
                        // Element might not be enabled, which is fine
                    }
                }
            }
            
            for (let i = 0; i < totalViewports && i < filesToLoad.length; i++) {
                const element = viewportRefs.current[i]?.current;
                if (!element) {
                    console.warn(`No element found for viewport ${i}`);
                    continue;
                }

                try {
                    const fileData = dicomFiles.length > 0 ? dicomFiles[i] : null;
                    console.log(`Processing viewport ${i}:`, fileData ? `Instance ${fileData.instanceNumber}` : 'No file data');
                    
                    // Check if this is an MP4 file that needs special handling
                    if (fileData && fileData.isMP4) {
                        // Handle MP4 files like DicomUnifiedViewer does
                        console.log(`Loading MP4 file in viewport ${i}`);
                        
                        // Clear the element first
                        while (element.firstChild) {
                            element.removeChild(element.firstChild);
                        }
                        
                        // For AHI MP4 files, create a video element
                        if (fileData.isAHI && fileData.file && fileData.file.type === 'video/mp4') {
                            const video = document.createElement('video');
                            video.src = URL.createObjectURL(fileData.file);
                            video.style.width = '100%';
                            video.style.height = '100%';
                            video.style.objectFit = 'contain';
                            video.style.backgroundColor = '#000';
                            video.controls = false;
                            video.loop = true;
                            
                            // Auto-play muted
                            video.muted = true;
                            video.addEventListener('loadedmetadata', () => {
                                video.play().catch(err => {
                                    console.log('Auto-play failed:', err);
                                });
                            });
                            
                            element.appendChild(video);
                        } else if (fileData.file) {
                            // For local DICOM MP4 files, extract and display
                            const reader = new FileReader();
                            reader.onload = async function(e) {
                                try {
                                    const arrayBuffer = e.target.result;
                                    const dicomData = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
                                    const pixelDataElement = dicomData.elements.x7fe00010;
                                    
                                    if (pixelDataElement) {
                                        const pixelDataOffset = pixelDataElement.dataOffset;
                                        const dataView = new DataView(arrayBuffer);
                                        let mp4Start = pixelDataOffset;
                                        const searchRange = Math.min(1000, arrayBuffer.byteLength - pixelDataOffset);
                                        
                                        for (let j = 0; j < searchRange - 3; j++) {
                                            if (String.fromCharCode(dataView.getUint8(pixelDataOffset + j)) === 'f' &&
                                                String.fromCharCode(dataView.getUint8(pixelDataOffset + j + 1)) === 't' &&
                                                String.fromCharCode(dataView.getUint8(pixelDataOffset + j + 2)) === 'y' &&
                                                String.fromCharCode(dataView.getUint8(pixelDataOffset + j + 3)) === 'p') {
                                                mp4Start = pixelDataOffset + j - 4;
                                                break;
                                            }
                                        }
                                        
                                        const mp4Data = arrayBuffer.slice(mp4Start);
                                        const blob = new Blob([mp4Data], { type: 'video/mp4' });
                                        
                                        const video = document.createElement('video');
                                        video.src = URL.createObjectURL(blob);
                                        video.style.width = '100%';
                                        video.style.height = '100%';
                                        video.style.objectFit = 'contain';
                                        video.style.backgroundColor = '#000';
                                        video.controls = false;
                                        video.loop = true;
                                        
                                        // Auto-play muted
                                        video.muted = true;
                                        video.addEventListener('loadedmetadata', () => {
                                            video.play().catch(err => {
                                                console.log('Auto-play failed:', err);
                                            });
                                        });
                                        
                                        element.appendChild(video);
                                    }
                                } catch (error) {
                                    console.error('Error extracting MP4:', error);
                                }
                            };
                            reader.readAsArrayBuffer(fileData.file);
                        }
                    } else {
                        // Handle regular DICOM images with Cornerstone
                        try {
                            // Check if this is a multiframe file that needs special handling
                            if (fileData && fileData.numberOfFrames > 1 && !fileData.isMP4) {
                                console.log(`Setting up multiframe DICOM playback for viewport ${i} with ${fileData.numberOfFrames} frames`);
                                await setupMultiframeViewport(element, fileData, i);
                            } else {
                                // Single frame or MP4 file - use standard display
                                // Enable cornerstone on the element
                                cornerstone.enable(element);

                                let imageId;
                                
                                // Determine the image ID based on the file type
                                if (fileData) {
                                    if (fileData.isAHI && fileData.imageId) {
                                        // AHI image with image ID
                                        imageId = fileData.imageId;
                                    } else if (fileData.file) {
                                        // Local file
                                        imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(fileData.file);
                                    }
                                } else if (isAHI && imageIds[i]) {
                                    imageId = imageIds[i];
                                } else if (files[i]) {
                                    imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(files[i]);
                                }

                                if (imageId) {
                                    // Load and display the image using Cornerstone
                                    const image = await cornerstone.loadImage(imageId);
                                    cornerstone.displayImage(element, image);
                                    
                                    // Set up viewport interaction
                                    setupViewportInteraction(element, i);
                                }
                            }

                        } catch (error) {
                            console.error(`Error loading image in viewport ${i}:`, error);
                        }
                    }

                } catch (error) {
                    console.error(`Error setting up viewport ${i}:`, error);
                }
            }
            
            // Mark loading as complete
            isLoadingRef.current = false;
            console.log('Finished loading viewports');
        };

        const setupViewportInteraction = (element, index) => {
            // Add event listeners for mouse interaction - but only for cornerstone elements
            element.addEventListener('mousedown', (e) => handleMouseDown(e, index));
            
            // Only add wheel and cornerstone events for cornerstone-enabled elements
            try {
                const enabledElement = cornerstone.getEnabledElement(element);
                if (enabledElement) {
                    element.addEventListener('wheel', (e) => handleMouseWheel(e, element));
                    
                    // Synchronize viewports
                    element.addEventListener('cornerstoneimagerendered', (e) => {
                        if (index === activeViewportIndex) {
                            synchronizeViewports(element, index);
                        }
                    });
                }
            } catch (e) {
                // Not a cornerstone element, skip wheel and cornerstone events
            }
        };

        const handleMouseDown = (e, index) => {
            // Always set active viewport index first
            console.log(`Setting active viewport to index: ${index}`);
            setActiveViewportIndex(index);
            
            if (e.button === 0) { // Left click for window/level
                const element = viewportRefs.current[index]?.current;
                if (!element) return;
                
                // Check if this element has cornerstone enabled before trying to use it
                try {
                    const enabledElement = cornerstone.getEnabledElement(element);
                    if (enabledElement) {
                        const startX = e.pageX;
                        const startY = e.pageY;
                        const startViewport = Object.assign({}, enabledElement.viewport);
                        
                        const mouseDrag = (e) => {
                            const deltaX = e.pageX - startX;
                            const deltaY = e.pageY - startY;
                            
                            const viewport = cornerstone.getViewport(element);
                            viewport.voi.windowWidth = startViewport.voi.windowWidth + deltaX;
                            viewport.voi.windowCenter = startViewport.voi.windowCenter + deltaY;
                            cornerstone.setViewport(element, viewport);
                        };
                        
                        const mouseUp = () => {
                            document.removeEventListener('mousemove', mouseDrag);
                            document.removeEventListener('mouseup', mouseUp);
                        };
                        
                        document.addEventListener('mousemove', mouseDrag);
                        document.addEventListener('mouseup', mouseUp);
                    }
                } catch (e) {
                    // Element is not cornerstone-enabled (probably a video), just set as active
                    console.log('Clicked on non-cornerstone element (video), setting as active viewport');
                }
            }
        };

        const handleMouseWheel = (e, element) => {
            e.preventDefault();
            const viewport = cornerstone.getViewport(element);
            
            if (e.deltaY < 0) {
                viewport.scale += 0.1;
            } else {
                viewport.scale -= 0.1;
            }
            
            viewport.scale = Math.max(0.1, Math.min(viewport.scale, 20));
            cornerstone.setViewport(element, viewport);
        };

        const synchronizeViewports = (sourceElement, sourceIndex) => {
            const sourceViewport = cornerstone.getViewport(sourceElement);
            
            viewportRefs.current.forEach((ref, index) => {
                if (index !== sourceIndex && ref.current) {
                    try {
                        const targetElement = ref.current;
                        if (cornerstone.getEnabledElement(targetElement)) {
                            const targetViewport = cornerstone.getViewport(targetElement);
                            targetViewport.scale = sourceViewport.scale;
                            targetViewport.translation = Object.assign({}, sourceViewport.translation);
                            targetViewport.voi = Object.assign({}, sourceViewport.voi);
                            cornerstone.setViewport(targetElement, targetViewport);
                        }
                    } catch (e) {
                        // Viewport might not be enabled
                    }
                }
            });
        };

        // Setup multiframe viewport for non-MP4 DICOM files
        const setupMultiframeViewport = async (element, fileData, viewportIndex) => {
            try {
                // Create image IDs for each frame
                const baseImageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(fileData.file);
                const imageIds = [];
                
                // Generate frame-specific image IDs
                for (let i = 0; i < fileData.numberOfFrames; i++) {
                    const frameImageId = `${baseImageId}?frame=${i}`;
                    imageIds.push(frameImageId);
                }

                console.log(`Created ${imageIds.length} frame image IDs for multiframe viewport ${viewportIndex}`);

                // Enable element with cornerstone-core
                cornerstone.enable(element);

                // Load and display the first frame
                const firstImage = await cornerstone.loadImage(imageIds[0]);
                cornerstone.displayImage(element, firstImage);

                // Store the image stack for playback
                element.imageIds = imageIds;
                element.currentImageIdIndex = 0;
                element.isMultiframe = true;
                element.numberOfFrames = fileData.numberOfFrames;
                element.currentFrame = 1;
                element.isPlaying = false;
                element.frameRate = 30;
                element.playbackInterval = null;

                // Set up viewport interaction
                setupViewportInteraction(element, viewportIndex);

                console.log(`Multiframe DICOM viewport ${viewportIndex} setup complete with ${fileData.numberOfFrames} frames`);

            } catch (error) {
                console.error(`Error setting up multiframe viewport ${viewportIndex}:`, error);
                throw error;
            }
        };
        
        loadImagesIntoViewports();
        
        return () => {
            // Cleanup viewports
            viewportRefs.current.forEach(ref => {
                if (ref.current) {
                    try {
                        cornerstone.disable(ref.current);
                    } catch (e) {
                        // Viewport might not be enabled
                    }
                }
            });
        };
    }, [gridLayout.rows, gridLayout.cols, imageIds.length, files.length, dicomFiles.length, isAHI, JSON.stringify(dicomFiles.map(f => f?.id || f?.imageId || f?.name))]);

    // Handle drawer state changes - resize Cornerstone viewports
    useEffect(() => {
        const resizeViewports = () => {
            // Add a small delay to ensure the CSS transition has completed
            setTimeout(() => {
                viewportRefs.current.forEach(ref => {
                    if (ref.current) {
                        try {
                            // Only resize if cornerstone is enabled on this element
                            const enabledElement = cornerstone.getEnabledElement(ref.current);
                            if (enabledElement) {
                                cornerstone.resize(ref.current);
                                console.log('Resized cornerstone viewport due to drawer state change');
                            }
                        } catch (e) {
                            // Element might not be enabled with cornerstone (could be video)
                        }
                    }
                });
            }, 350); // Match the CSS transition duration (0.3s) + small buffer
        };

        resizeViewports();
    }, [drawerOpen]);

    // Add keyboard event handling for grid viewer
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle keyboard events if the grid viewer is focused or no input is focused
            const activeElement = document.activeElement;
            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                return; // Don't handle keyboard events when typing in inputs
            }

            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    togglePlayPauseActiveViewport();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    navigateFrameActiveViewport('previous');
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    navigateFrameActiveViewport('next');
                    break;
                default:
                    break;
            }
        };

        // Add event listener to document
        document.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeViewportIndex]); // Re-attach when active viewport changes

    // Notify parent when active viewport changes (for playback state updates)
    useEffect(() => {
        if (onActiveViewportChange) {
            onActiveViewportChange(activeViewportIndex);
        }
    }, [activeViewportIndex, onActiveViewportChange]);

    const resetAllViewports = () => {
        viewportRefs.current.forEach(ref => {
            if (ref.current) {
                try {
                    cornerstone.reset(ref.current);
                } catch (e) {
                    // Viewport might not be enabled
                }
            }
        });
    };

    const invertAllViewports = () => {
        viewportRefs.current.forEach(ref => {
            if (ref.current) {
                try {
                    const viewport = cornerstone.getViewport(ref.current);
                    viewport.invert = !viewport.invert;
                    cornerstone.setViewport(ref.current, viewport);
                } catch (e) {
                    // Viewport might not be enabled
                }
            }
        });
    };

    // Playback control functions for the active viewport
    const togglePlayPauseActiveViewport = () => {
        const activeElement = viewportRefs.current[activeViewportIndex]?.current;
        if (!activeElement) return;

        // Check if this is a video element
        const videoElement = activeElement.querySelector('video');
        if (videoElement) {
            if (videoElement.paused) {
                videoElement.play().catch(err => {
                    console.error('Error playing video:', err);
                    // Try muted if unmuted playback fails
                    videoElement.muted = true;
                    videoElement.play().catch(err2 => {
                        console.error('Error playing video even when muted:', err2);
                    });
                });
            } else {
                videoElement.pause();
            }
        }
    };

    const navigateFrameActiveViewport = (direction) => {
        const activeElement = viewportRefs.current[activeViewportIndex]?.current;
        if (!activeElement) return;

        // Check if this is a video element
        const videoElement = activeElement.querySelector('video');
        if (videoElement && videoElement.duration) {
            // Handle MP4 video navigation
            videoElement.pause();

            // Get current file info to determine total frames
            const activeFileData = dicomFiles[activeViewportIndex];
            const totalFrames = activeFileData?.numberOfFrames || 40;

            // Calculate frame duration
            const frameDuration = videoElement.duration / totalFrames;
            
            // Calculate current frame
            const currentFrame = Math.floor(videoElement.currentTime / frameDuration) + 1;
            
            // Calculate target frame
            let targetFrame;
            if (direction === 'next') {
                targetFrame = currentFrame + 1;
                if (targetFrame > totalFrames) targetFrame = 1;
            } else {
                targetFrame = currentFrame - 1;
                if (targetFrame < 1) targetFrame = totalFrames;
            }

            // Set video time to target frame
            const targetTime = (targetFrame - 1) * frameDuration;
            videoElement.currentTime = targetTime;
        } else if (activeElement.isMultiframe && activeElement.imageIds) {
            // Handle multiframe DICOM navigation
            let targetFrame;
            if (direction === 'next') {
                targetFrame = activeElement.currentFrame + 1;
                if (targetFrame > activeElement.numberOfFrames) targetFrame = 1;
            } else {
                targetFrame = activeElement.currentFrame - 1;
                if (targetFrame < 1) targetFrame = activeElement.numberOfFrames;
            }

            navigateMultiframeToFrame(activeElement, targetFrame);
        }
    };

    // Navigate to a specific frame in multiframe DICOM files
    const navigateMultiframeToFrame = async (element, frameNumber) => {
        if (!element || !element.imageIds || !element.isMultiframe) return;

        // Clamp frame number to valid range
        let targetFrame = frameNumber;
        if (targetFrame < 1) targetFrame = element.numberOfFrames;
        if (targetFrame > element.numberOfFrames) targetFrame = 1;

        const imageIndex = targetFrame - 1;
        
        try {
            // Load and display the target frame
            const image = await cornerstone.loadImage(element.imageIds[imageIndex]);
            cornerstone.displayImage(element, image);
            
            // Update current frame state
            element.currentImageIdIndex = imageIndex;
            element.currentFrame = targetFrame;
            
            console.log(`Navigated multiframe viewport to frame ${targetFrame} of ${element.numberOfFrames}`);
        } catch (error) {
            console.error('Error navigating to multiframe frame:', error);
        }
    };

    const handleFrameSliderChangeActiveViewport = (e) => {
        const frameNumber = parseInt(e.target.value, 10);
        const activeElement = viewportRefs.current[activeViewportIndex]?.current;
        if (!activeElement) return;

        // Check if this is a video element
        const videoElement = activeElement.querySelector('video');
        if (videoElement && videoElement.duration) {
            // Handle MP4 video frame slider
            videoElement.pause();

            // Get current file info to determine total frames
            const activeFileData = dicomFiles[activeViewportIndex];
            const totalFrames = activeFileData?.numberOfFrames || 40;

            // Calculate and set video time
            const frameDuration = videoElement.duration / totalFrames;
            const targetTime = (frameNumber - 1) * frameDuration;
            videoElement.currentTime = targetTime;
        } else if (activeElement.isMultiframe && activeElement.imageIds) {
            // Handle multiframe DICOM frame slider
            navigateMultiframeToFrame(activeElement, frameNumber);
        }
    };

    const handleFrameRateChangeActiveViewport = (e) => {
        const newFrameRate = parseInt(e.target.value, 10);
        const activeElement = viewportRefs.current[activeViewportIndex]?.current;
        if (!activeElement) return;

        // Check if this is a video element
        const videoElement = activeElement.querySelector('video');
        if (videoElement && videoElement.duration) {
            // Get current file info to determine total frames
            const activeFileData = dicomFiles[activeViewportIndex];
            const totalFrames = activeFileData?.numberOfFrames || 40;

            // Calculate playback rate
            const naturalFrameRate = totalFrames / videoElement.duration;
            videoElement.playbackRate = newFrameRate / naturalFrameRate;
        }
    };

    // Get current playback state for active viewport
    const getActiveViewportPlaybackState = () => {
        const activeElement = viewportRefs.current[activeViewportIndex]?.current;
        if (!activeElement) return { isPlaying: false, currentFrame: 1, totalFrames: 1, frameRate: 30 };

        const videoElement = activeElement.querySelector('video');
        if (videoElement && videoElement.duration) {
            // Handle MP4 video playback state
            const activeFileData = dicomFiles[activeViewportIndex];
            const totalFrames = activeFileData?.numberOfFrames || 40;
            const frameDuration = videoElement.duration / totalFrames;
            const currentFrame = Math.floor(videoElement.currentTime / frameDuration) + 1;
            const naturalFrameRate = totalFrames / videoElement.duration;
            const currentFrameRate = Math.round(videoElement.playbackRate * naturalFrameRate);

            return {
                isPlaying: !videoElement.paused,
                currentFrame: currentFrame,
                totalFrames: totalFrames,
                frameRate: currentFrameRate
            };
        } else if (activeElement.isMultiframe && activeElement.imageIds) {
            // Handle multiframe DICOM playback state
            return {
                isPlaying: activeElement.isPlaying || false,
                currentFrame: activeElement.currentFrame || 1,
                totalFrames: activeElement.numberOfFrames || 1,
                frameRate: activeElement.frameRate || 30
            };
        }

        return { isPlaying: false, currentFrame: 1, totalFrames: 1, frameRate: 30 };
    };

    // Get active viewport file info
    const getActiveViewportFileInfo = () => {
        if (activeViewportIndex >= 0 && activeViewportIndex < dicomFiles.length) {
            const activeFile = dicomFiles[activeViewportIndex];
            return activeFile;
        }
        return null;
    };

    // Expose playback control functions
    React.useImperativeHandle(ref, () => ({
        togglePlayPause: togglePlayPauseActiveViewport,
        navigateFrame: navigateFrameActiveViewport,
        handleFrameSliderChange: handleFrameSliderChangeActiveViewport,
        handleFrameRateChange: handleFrameRateChangeActiveViewport,
        getPlaybackState: getActiveViewportPlaybackState,
        getActiveViewportFileInfo: getActiveViewportFileInfo,
        getActiveViewportIndex: () => activeViewportIndex,
        resetViewports: resetAllViewports,
        invertViewports: invertAllViewports
    }));

    return (
        <div className="dicom-grid-viewer">
            {/* Grid container */}
            <div 
                ref={containerRef}
                className="grid-container"
                style={{
                    display: 'grid',
                    gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
                    gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
                    gap: '4px',
                    width: '100%',
                    height: '100%', // Full height
                    padding: '4px'
                }}
            >
                {Array.from({ length: gridLayout.rows * gridLayout.cols }).map((_, index) => {
                    const totalFiles = dicomFiles.length > 0 ? dicomFiles.length : (isAHI ? imageIds.length : files.length);
                    const hasImage = index < totalFiles;
                    return (
                        <div
                            key={index}
                            className={`viewport-cell ${index === activeViewportIndex ? 'active' : ''}`}
                            style={{
                                position: 'relative',
                                overflow: 'hidden',
                                background: '#000',
                                border: index === activeViewportIndex ? '2px solid #0078d4' : '1px solid #333',
                                cursor: 'pointer'
                            }}
                            onClick={() => {
                                console.log(`Viewport cell ${index} clicked`);
                                setActiveViewportIndex(index);
                            }}
                        >
                            {hasImage ? (
                                <>
                                    <div 
                                        ref={viewportRefs.current[index]}
                                        className="viewport-element"
                                        style={{ 
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    />
                                    <div className="viewport-info" style={{
                                        position: 'absolute',
                                        top: '5px',
                                        left: '5px',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: '#fff',
                                        padding: '2px 6px',
                                        borderRadius: '3px',
                                        fontSize: '12px',
                                        pointerEvents: 'none'
                                    }}>
                                        <span>{dicomFiles[index]?.instanceNumber || (index + 1)}</span>
                                    </div>
                                    {/* Study boundary overlays */}
                                    {(() => {
                                        const currentFile = dicomFiles[index];
                                        if (!currentFile) return null;
                                        
                                        // Find the first and last files in the ENTIRE STUDY by instance number
                                        // Use allStudyFiles if available, otherwise fall back to current grid files
                                        const studyFiles = allStudyFiles.length > 0 ? allStudyFiles : dicomFiles;
                                        const sortedFiles = studyFiles.slice().sort((a, b) => {
                                            const aNum = a.instanceNumber || 0;
                                            const bNum = b.instanceNumber || 0;
                                            return aNum - bNum;
                                        });
                                        
                                        const firstFile = sortedFiles[0];
                                        const lastFile = sortedFiles[sortedFiles.length - 1];
                                        
                                        const isFirstImage = currentFile.id === firstFile.id;
                                        const isLastImage = currentFile.id === lastFile.id;
                                        
                                        // Only show overlays when there's actual wrapping happening
                                        // Don't show "First Image In Study" if it's in the natural first position (index 0)
                                        // Only show "Last Image In Study" if it's truly the last image in the study
                                        
                                        let showOverlay = false;
                                        let overlayText = '';
                                        let borderColor = '';
                                        
                                        if (isFirstImage && index !== 0) {
                                            // Only show "First Image In Study" if it's NOT in the first viewport position
                                            // This indicates wrapping has occurred
                                            showOverlay = true;
                                            overlayText = 'First Image In Study';
                                            borderColor = '#4CAF50';
                                        } else if (isLastImage) {
                                            // Always show "Last Image In Study" when it's truly the last image
                                            showOverlay = true;
                                            overlayText = 'Last Image In Study';
                                            borderColor = '#FF9800';
                                        }
                                        
                                        if (showOverlay) {
                                            return (
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '10px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    background: 'rgba(0,0,0,0.8)',
                                                    color: '#fff',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    pointerEvents: 'none',
                                                    whiteSpace: 'nowrap',
                                                    border: `1px solid ${borderColor}`
                                                }}>
                                                    {overlayText}
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </>
                            ) : (
                                <div className="empty-viewport" style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    color: '#666',
                                    fontSize: '14px'
                                }}>
                                    <span>No Image</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default DicomGridViewer;
